/* Muaazon 3D Discovery - isolated progressive enhancement. No commerce state changes. */
(()=> {
  'use strict';
  if (window.__muaazonDiscoveryEngine) return;
  window.__muaazonDiscoveryEngine = true;
  const instances = new WeakMap();
  const gsapUrl = document.querySelector('.mz-curve[data-gsap-src]')?.dataset?.gsapSrc || '';
  const mod = (n, length) => ((n % length) + length) % length;
  const clamp = (n, low, high) => Math.max(low, Math.min(high, n));
  function loadGsap() {
    if (window.gsap) return Promise.resolve(window.gsap);
    if (window.__muaazonGsapPromise) return window.__muaazonGsapPromise;
    if (!gsapUrl) return Promise.resolve(null);
    window.__muaazonGsapPromise = new Promise(resolve => {
      const existing = document.querySelector('script[data-muaazon-gsap-loader]');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.gsap || null), {once:true});
        existing.addEventListener('error', () => resolve(null), {once:true});
        return;
      }
      const s = document.createElement('script');
      s.async = true;
      s.src = gsapUrl;
      s.dataset.muaazonGsapLoader = 'true';
      s.onload = () => resolve(window.gsap || null);
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    });
    return window.__muaazonGsapPromise;
  }
  function setup(root) {
    if (!root || instances.has(root)) return;
    const cards = Array.from(root.querySelectorAll('.mz-curve__card'));
    const viewport = root.querySelector('[data-viewport]');
    const dots = root.querySelector('[data-dots]');
    const status = root.querySelector('[data-status]');
    const prev = root.querySelector('[data-prev]');
    const next = root.querySelector('[data-next]');
    if (!cards.length || !viewport || !dots || !prev || !next) return;
    const signalController = new AbortController();
    const signal = signalController.signal;
    const reduceMedia = matchMedia('(prefers-reduced-motion: reduce)');
    let reduced = reduceMedia.matches;
    let progress = 0;
    let animation = null;
    let frame = 0;
    let drag = null;
    let suppressClick = false;
    let intersection = null;
    let resizeObserver = null;
    const count = cards.length;
    const dotButtons = cards.map((card, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('data-mz-glass-exclude','');
      const label=card.dataset.cardLabel || card.getAttribute('aria-label') || 'Category';
      dot.setAttribute('aria-label','Go to '+label+', slide '+(i+1)+' of '+count);
      dot.setAttribute('aria-controls',viewport.id);
      dot.addEventListener('click', () => goTo(i, true), {signal});
      dots.append(dot);
      return dot;
    });
    function stepSize() {
      const width = viewport.clientWidth || window.innerWidth;
      const editorial = root.classList.contains('mz-curve--editorial');
      if (editorial) return Math.min(width * (width < 750 ? 0.55 : 0.31), width < 750 ? 245 : 300);
      return Math.min(width * (width < 750 ? 0.51 : 0.34), width < 750 ? 235 : 315);
    }
    function normalizedOffset(i) {
      let diff = i-progress;
      while (diff > count / 2) diff -= count;
      while (diff < -count / 2) diff += count;
      return diff;
    }
    function paint() {
      const mobile = viewport.clientWidth < 750;
      const editorial = root.classList.contains('mz-curve--editorial');
      const step = stepSize();
      let active = mod(Math.round(progress), count);
      cards.forEach((card,i) => {
        const d = normalizedOffset(i);
        const magnitude = Math.abs(d);
        const visible = magnitude < (mobile ? 1.7 : (editorial ? 2.7 : 3.2));
        const x = d * step;
        const z = -Math.min(magnitude,3) * (editorial ? (mobile ? 72 : 135) : (mobile ? 65 : 95));
        const y = Math.min(magnitude,3) * (editorial ? (mobile ? 10 : 28) : (mobile ? 3 : 5));
        const angle = -d * (editorial ? (mobile ? 8 : 11) : (mobile ? 12 : 17));
        const scale = 1 - Math.min(magnitude,3) * (editorial ? (mobile ? 0.10 : 0.14) : 0.075);
        card.style.transform = 'translate3d(calc(-50% + '+x.toFixed(2)+'px),'+y.toFixed(2)+'px,'+z.toFixed(2)+'px) rotateY('+angle.toFixed(2)+'deg) scale('+scale.toFixed(4)+')';
        card.style.opacity = visible ? String(clamp(1-magnitude*0.14,0.55,1)) : '0';
        card.style.zIndex = String(100-Math.round(magnitude*10));
        card.style.pointerEvents = visible ? 'auto' : 'none';
        card.setAttribute('aria-hidden', visible ? 'false' : 'true');
        card.tabIndex = active===i ? 0 : -1;
        card.dataset.active = String(active===i);
        if(active===i)card.setAttribute('aria-current','true');else card.removeAttribute('aria-current');
      });
      dotButtons.forEach((dot,i) => {
        dot.setAttribute('aria-current', String(active===i));
      });
      root.dataset.activeIndex = String(active);
    }
    function announce() {
      if (!status) return;
      const i = mod(Math.round(progress),count);
      status.textContent = (cards[i].dataset.cardLabel || 'Category')+', slide '+(i+1)+' of '+count;
    }
    function stopAnimation() {
      if (animation && typeof animation.kill === 'function') animation.kill();
      animation = null;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    }
    function settle(target, userInitiated) {
      stopAnimation();
      if (reduced || Math.abs(target-progress)<0.001) {
        progress = mod(target,count);
        paint();
        if (userInitiated) announce();
        return;
      }
      const start = progress;
      const duration = 580;
      const end = () => {
        progress = mod(target,count);
        paint();
        if (userInitiated) announce();
      };
      if (window.gsap && root.dataset.gsap !== 'off') {
        const state = {value:progress};
        animation = window.gsap.to(state, {
          value:target, duration:0.64, ease:'power3.out',
          overwrite:true, onUpdate:() => { progress=state.value; paint(); },
          onComplete:end
        });
      } else {
        const began = performance.now();
        function tick(now) {
          const t=clamp((now-began)/duration,0,1);
          progress=start+(target-start)*(1-Math.pow(1-t,3));
          paint();
          if(t<1)frame=requestAnimationFrame(tick);else end();
        }
        frame=requestAnimationFrame(tick);
      }
    }
    function goTo(index, userInitiated) {
      const target = index + Math.round((progress-index)/count)*count;
      settle(target, userInitiated);
    }
    function relative(direction) {
      settle(Math.round(progress)+direction,true);
    }
    prev.addEventListener('click',()=>relative(-1),{signal});
    next.addEventListener('click',()=>relative(1),{signal});
    viewport.addEventListener('keydown',event=>{
      if(event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      relative(event.key==='ArrowRight'?1:-1);
    },{signal});
    viewport.addEventListener('pointerdown',event=>{
      if(root.dataset.drag==='off' || event.button!==0 || (event.pointerType==='mouse' && event.target.closest('button'))) return;
      drag={id:event.pointerId,x:event.clientX,start:progress,time:performance.now(),lastX:event.clientX,lastTime:performance.now(),velocity:0,moved:false};
    },{signal});
    viewport.addEventListener('pointermove',event=>{
      if(!drag || drag.id!==event.pointerId) return;
      const dx=event.clientX-drag.x;
      if(Math.abs(dx)>8 && !drag.moved) {
        stopAnimation();
        drag.start=progress;
        drag.moved=true;
        if(viewport.setPointerCapture) { try{viewport.setPointerCapture(event.pointerId)}catch(e){} }
      }
      if(!drag.moved) return;
      event.preventDefault();
      const now=performance.now();
      const dt=Math.max(now-drag.lastTime,1);
      drag.velocity=clamp(-(event.clientX-drag.lastX)/stepSize()/dt,-0.015,0.015);
      drag.lastX=event.clientX;drag.lastTime=now;
      progress=drag.start-dx/stepSize();
      paint();
    },{signal});
    function endDrag(event) {
      if(!drag || drag.id!==event.pointerId)return;
      if(drag.moved){
        suppressClick=true;
        const rawTarget=Math.round(progress + clamp(drag.velocity*150,-1.25,1.25));
        settle(rawTarget,true);
        setTimeout(()=>{suppressClick=false},650);
      }
      if(viewport.releasePointerCapture && viewport.hasPointerCapture && viewport.hasPointerCapture(event.pointerId)){
        try{viewport.releasePointerCapture(event.pointerId)}catch(e){}
      }
      drag=null;
    }
    viewport.addEventListener('pointerup',endDrag,{signal});
    viewport.addEventListener('pointercancel',endDrag,{signal});
    viewport.addEventListener('click',event=>{
      if(!suppressClick || !event.target.closest('.mz-curve__card'))return;
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressClick=false;
    },{signal,capture:true});
    const handleResize=()=>{stopAnimation();progress=mod(Math.round(progress),count);paint()};
    if('ResizeObserver' in window){resizeObserver=new ResizeObserver(handleResize);resizeObserver.observe(viewport)}else window.addEventListener('resize',handleResize,{signal,passive:true});
    const changeMotion=()=>{reduced=reduceMedia.matches;stopAnimation();progress=mod(Math.round(progress),count);paint()};
    if(reduceMedia.addEventListener)reduceMedia.addEventListener('change',changeMotion,{signal});
    const instance={destroy(){
      stopAnimation();
      if(intersection)intersection.disconnect();
      resizeObserver?.disconnect();
      signalController.abort();
      dots.replaceChildren();
      cards.forEach(card=>{card.style.transform='';card.style.opacity='';card.style.zIndex='';card.style.pointerEvents='';card.removeAttribute('aria-hidden');card.removeAttribute('data-active');card.removeAttribute('tabindex')});
      instances.delete(root);
    }};
    instances.set(root,instance);
    paint();
    if(root.dataset.gsap!=='off' && !reduced){
      const preload=()=>{
        loadGsap().then(()=>{if(!signal.aborted)paint()});
      };
      if('IntersectionObserver' in window){
        intersection=new IntersectionObserver(entries=>{
          if(entries.some(entry=>entry.isIntersecting)){intersection.disconnect();intersection=null;preload()}
        },{rootMargin:'300px'});
        intersection.observe(root);
      } else preload();
    }
  }
  document.querySelectorAll('.mz-curve').forEach(setup);
  document.addEventListener('shopify:section:load',event=>{
    if(event.target.matches && event.target.matches('.mz-curve'))setup(event.target);
    if(event.target.querySelectorAll)event.target.querySelectorAll('.mz-curve').forEach(setup);
  });
  document.addEventListener('shopify:section:unload',event=>{
    if(event.target.matches && event.target.matches('.mz-curve'))instances.get(event.target)?.destroy();
    if(event.target.querySelectorAll)event.target.querySelectorAll('.mz-curve').forEach(root=>instances.get(root)?.destroy());
  });
})();